import { useAuth0 } from '@auth0/auth0-react';
import { ShoppingCartOutlined } from '@material-ui/icons';
import companyApi, { CompanyRenewalStatus } from 'api/auth/company';
import AdminDialog from 'components/admin-dialog';
import { useUser } from 'providers/user/use-user';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigation } from 'react-navi';

const RENEWAL_CHECK_KEY = 'sg_renewal_check_done';

/**
 * Display a renewal dialog for company users if their company renewal status is Due or Overdue.
 * This check runs once per session after login to ensure users address renewal requirements.
 */
export const CompanyRenewalRedirector: React.FC = () => {
  const user = useUser();
  const { isAuthenticated } = useAuth0();
  const navigation = useNavigation();
  const checkInProgress = useRef(false);
  const hasCheckedRef = useRef(false);
  const [showRenewalDialog, setShowRenewalDialog] = useState(false);
  const [renewalStatus, setRenewalStatus] = useState<CompanyRenewalStatus | null>(null);

  // Clear the check flag when user is not authenticated or not a company user
  useEffect(() => {
    if (!isAuthenticated || !user.isCompanyUser || !user.companyId) {
      try {
        sessionStorage.removeItem(RENEWAL_CHECK_KEY);
        hasCheckedRef.current = false;
      } catch (error) {
        // Ignore sessionStorage errors
      }
    }
  }, [isAuthenticated, user.isCompanyUser, user.companyId]);

  useEffect(() => {
    const checkAndRedirect = async () => {
      // Prevent duplicate checks - check both ref and sessionStorage
      if (checkInProgress.current || hasCheckedRef.current) {
        return;
      }

      // Only check if user is authenticated, has loaded data, is a company user, and has a company ID
      if (!isAuthenticated || !user.hasLoadedData || !user.isCompanyUser || !user.companyId) {
        return;
      }

      // Check if we've already done the renewal check this session
      try {
        const alreadyChecked = sessionStorage.getItem(RENEWAL_CHECK_KEY);
        if (alreadyChecked) {
          hasCheckedRef.current = true;
          return;
        }
      } catch (error) {
        // SessionStorage access failed, continue with check
      }

      checkInProgress.current = true;

      try {
        const route = await navigation.getRoute();
        const currentPath = route.url.pathname;

        // Don't redirect if already on the target page or invoice page
        if (currentPath.startsWith('/producer/my-company/flocks') || currentPath === '/invoice') {
          hasCheckedRef.current = true;
          try {
            sessionStorage.setItem(RENEWAL_CHECK_KEY, 'true');
          } catch (error) {
            // Ignore sessionStorage errors
          }
          checkInProgress.current = false;
          return;
        }

        // Check company renewal status
        const renewalStatusResult = await companyApi.getCompanyRenewalStatus(user.companyId);

        // Mark as checked before showing dialog
        hasCheckedRef.current = true;
        try {
          sessionStorage.setItem(RENEWAL_CHECK_KEY, 'true');
        } catch (error) {
          // Ignore sessionStorage errors
        }

        // Show dialog if status is Due or Overdue
        if (
          renewalStatusResult.renewalStatus === CompanyRenewalStatus.Due ||
          renewalStatusResult.renewalStatus === CompanyRenewalStatus.Overdue
        ) {
          setRenewalStatus(renewalStatusResult.renewalStatus);
          setShowRenewalDialog(true);
        }
      } catch (error) {
        // Silently fail - don't block user navigation on error
        console.error('Error checking company renewal status:', error);
      } finally {
        checkInProgress.current = false;
      }
    };

    if (isAuthenticated && user.hasLoadedData && user.isCompanyUser && user.companyId) {
      checkAndRedirect();
    }
  }, [isAuthenticated, user.hasLoadedData, user.isCompanyUser, user.companyId, navigation]);

  const handleCloseDialog = () => {
    setShowRenewalDialog(false);
  };

  const handlePayRenewal = async () => {
    setShowRenewalDialog(false);
    await navigation.navigate('/producer/my-company/flock-fees');
  };

  const isOverdue = renewalStatus === CompanyRenewalStatus.Overdue;

  return (
    <>
      <AdminDialog
        open={showRenewalDialog}
        title={isOverdue ? 'Your Annual Flock Fees Are Overdue!' : 'Your Annual Flock Fees Are Due Soon!'}
        labelNo='Close'
        labelYes='Pay Renewal'
        colorYes='pay'
        variantNo='outlined'
        variantYes='contained'
        iconYes={<ShoppingCartOutlined />}
        onNo={handleCloseDialog}
        onYes={handlePayRenewal}
      >
        <p>
          {isOverdue
            ? 'Your flock data is now unavailable. Please renew your annual flock fees to restore access to your flock data.'
            : 'Please renew your annual flock fees to ensure uninterrupted access to your flock data.'}
        </p>
      </AdminDialog>
    </>
  );
};
